import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { formatDateTime } from '@/lib/i18n';
import {
  apiError,
  ForecastSuggestionKind,
  ForecastSuggestionStatus,
  type ForecastSuggestion,
} from './lib';

/**
 * The suggestion queue. ADR-0031 §2.4 — nothing here can turn into a par
 * change or a shift by itself. `INVENTORY_PAR` names the EXISTING screen
 * that applies it (`POST /replenishment/pars`, its own `replenishment.manage`
 * key); `LABOUR_HEADCOUNT` has no apply step at all, ever — acknowledging one
 * only records that a manager saw the number. Neither button here is an
 * "Apply".
 */
export default function SuggestionsPanel({ branchId }: { branchId: string }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { prompt } = useConfirm();
  const canManage = can('forecasting.manage');
  // ADR-0031 §2.4 grants `forecasting.manage` no reach into `replenishment.*`
  // on purpose — applying a par change is gated a THIRD, independent way, on
  // the key `POST /replenishment/pars` itself requires. An `INVENTORY_MANAGER`
  // holds this and only `forecasting.view`; a `BRANCH_MANAGER` holds
  // `forecasting.manage` and only `replenishment.view`. Neither implies the
  // other, so both are checked and rendered independently below.
  const canApplyPar = can('replenishment.manage');

  const [status, setStatus] = useState<ForecastSuggestionStatus>(ForecastSuggestionStatus.PENDING);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { data, loading, error, reload } = useApi<unknown>('/forecasting/suggestions', {
    branchId,
    status,
    limit: 50,
  });
  const { rows } = rowsOf<ForecastSuggestion>(data);

  const acknowledge = async (id: string) => {
    setBusyId(id);
    try {
      await api.patch(`/forecasting/suggestions/${id}/acknowledge`);
      toast.success(t('forecasting.suggestions.acknowledged'));
      reload();
    } catch (e) {
      toast.error(apiError(e, t('forecasting.suggestions.actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (id: string) => {
    const reason = await prompt({
      title: t('forecasting.suggestions.dismissTitle'),
      label: t('forecasting.suggestions.dismissReason'),
      placeholder: t('forecasting.suggestions.dismissPlaceholder'),
      confirmLabel: t('forecasting.suggestions.dismiss'),
    });
    if (reason === null) return;
    setBusyId(id);
    try {
      await api.patch(`/forecasting/suggestions/${id}/dismiss`, reason ? { reason } : {});
      toast.success(t('forecasting.suggestions.dismissed'));
      reload();
    } catch (e) {
      toast.error(apiError(e, t('forecasting.suggestions.actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle>{t('forecasting.suggestions.title')}</CardTitle>
        <Field label={t('forecasting.suggestions.status')} controlId="suggestion-status">
          <Select
            id="suggestion-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ForecastSuggestionStatus)}
            className="w-40"
          >
            {Object.values(ForecastSuggestionStatus).map((s) => (
              <option key={s} value={s}>
                {t(`forecasting.suggestions.statusValue.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <LoadingState label={t('common.loading')} />
        ) : error ? (
          <ErrorState message={t('forecasting.suggestions.loadFailed')} onRetry={reload} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={
              status === ForecastSuggestionStatus.PENDING
                ? t('forecasting.suggestions.emptyPending')
                : t('forecasting.suggestions.empty')
            }
            hint={t('forecasting.suggestions.emptyHint')}
          />
        ) : (
          <ul className="space-y-3">
            {rows.map((s) => (
              <li key={s.id} className="rounded-lg border p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Badge tone={s.kind === ForecastSuggestionKind.INVENTORY_PAR ? 'brand' : 'info'}>
                      {t(`forecasting.suggestions.kind.${s.kind}`)}
                    </Badge>
                    <span className="text-xs text-ink-muted">
                      {formatDateTime(s.createdAt, i18n.language)}
                    </span>
                  </div>
                  {status === ForecastSuggestionStatus.PENDING && canManage && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === s.id}
                        onClick={() => void acknowledge(s.id)}
                      >
                        {t('forecasting.suggestions.acknowledge')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyId === s.id}
                        onClick={() => void dismiss(s.id)}
                      >
                        {t('forecasting.suggestions.dismiss')}
                      </Button>
                    </div>
                  )}
                  {status === ForecastSuggestionStatus.ACKNOWLEDGED && (
                    <Badge tone="success">{t('forecasting.suggestions.statusValue.ACKNOWLEDGED')}</Badge>
                  )}
                  {status === ForecastSuggestionStatus.DISMISSED && (
                    <Badge tone="neutral">{t('forecasting.suggestions.statusValue.DISMISSED')}</Badge>
                  )}
                </div>
                <p className="mt-2 text-sm">{s.reason}</p>
                {s.kind === ForecastSuggestionKind.INVENTORY_PAR ? (
                  <p className="mt-2 text-xs text-ink-muted">
                    {t('forecasting.suggestions.applyHintInventory')}{' '}
                    {/* The suggestion never writes a par level itself (ADR-0031 §2.4) —
                        `replenishment.manage` is what actually lets someone apply this
                        number, independent of `forecasting.manage` above, so the link
                        only appears for an actor who could use it. */}
                    {canApplyPar && (
                      <Link
                        to="/central-kitchen/replenishment"
                        className="font-medium text-accent underline"
                      >
                        {t('forecasting.suggestions.applyLink')}
                      </Link>
                    )}
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-ink-muted">
                    {t('forecasting.suggestions.applyHintLabour')}
                  </p>
                )}
                {s.dismissReason && (
                  <p className="mt-2 text-xs italic text-ink-muted">
                    {t('forecasting.suggestions.dismissedWithReason', { reason: s.dismissReason })}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
